/**
 * [PROD-1204] The built-in "Chats" search kind — the cross-platform half of
 * chat search.
 *
 * Registered by `<AgentTopbar>`, so searching your whole chat history lands in
 * every host app that mounts the embed (boombox, customers, factory, hq, menus,
 * tasker, rfp-gremlin) and the script-tag widget, with ZERO host-app edits.
 *
 * A `SearchableKind` rather than bespoke plumbing, because the palette's runner
 * already provides everything this needs: the 250ms debounce
 * (`use-live-search.ts`), a per-kind AbortSignal timeout whose failures are
 * swallowed into a missing section rather than a host-app toast
 * (`search-runner.ts`), section rendering, keyboard nav, `MessageSquare` for
 * kind `chats` (`kind-icon.tsx`), and — critically — the `live-` section-id
 * prefix that stops `agent-search.tsx` / `empty-body.tsx` re-filtering these
 * hits by title. A body match has a title that does NOT contain the query, i.e.
 * exactly the rows chat search exists to surface.
 */

import type { SearchableHit, SearchableKind } from '../types'
import { buildAgentChatUrl } from './agent-app-url'
import { apiFetch } from './api-client'
import { relativeTime } from './recent-conversations'

/** Mirrors MIN_CHAT_SEARCH_LENGTH on the agent server. Gated here too so a
 *  one-character keystroke across seven host apps doesn't fire a request the
 *  server is guaranteed to answer with []. */
export const MIN_CHAT_SEARCH_LENGTH = 2

interface ChatHit {
  id: string
  title: string | null
  updatedAt: string
}

/**
 * `agentAppUrl` deep-links each hit at agent.ordo.com. Hits open in a new tab
 * (`target: '_blank'`) because the chat lives in a different app than the host
 * the palette is mounted in — the same contract cross-app hits already use.
 */
export function chatSearchKind(agentAppUrl?: string): SearchableKind {
  return {
    kind: 'chats',
    label: 'Chats',
    target: '_blank',
    search: async (query: string, signal: AbortSignal): Promise<SearchableHit[]> => {
      const q = query.trim()
      if (q.length < MIN_CHAT_SEARCH_LENGTH) {
        return []
      }
      // Unscoped by hostApp on purpose: RECENT above is host-scoped, and
      // reaching the chats RECENT hides is the whole ask — "search across ALL
      // of their past Agent chats".
      const rows = await apiFetch<ChatHit[]>(`/api/recall/conversations?q=${encodeURIComponent(q)}`, { signal })
      return rows.map((c) => ({
        id: c.id,
        title: c.title ?? 'Untitled chat',
        // Timestamp rather than the snippet: the palette's meta column does not
        // shrink, so a long excerpt there crushes the title on mobile in every
        // host. Snippets ship on the agent app's own /chats page.
        meta: relativeTime(c.updatedAt),
        href: buildAgentChatUrl(c.id, agentAppUrl),
      }))
    },
  }
}
